import styled from 'styled-components';

/* =========================================================================
 * 회원가입 모바일 공통 셸
 *  - 코드입력 / 이메일 인증 / 정보입력(3스텝 위저드) 가 공유한다.
 *  - 상단 "회원가입" 헤더 + 하단 고정 액션 버튼.
 * ======================================================================= */

export const RegShell = styled.div`
  min-height: 100dvh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.White};
  padding-bottom: 60px; /* 하단 고정 버튼 영역 확보 */
`;

export const RegHeader = styled.header`
  position: relative;
  flex-shrink: 0;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${({ theme }) => theme.color.Gray20};
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Black};
`;

/* 회색 안내 배너 (코드입력·담당자 정보 등)
   배경은 풀폭 유지, 텍스트만 본문 640px 컬럼과 좌측 정렬(태블릿 밴드) */
export const Banner = styled.div`
  flex-shrink: 0;
  background: #f6f6f6;
  padding: 18px max(20px, calc(50% - 300px));
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Black};

  b,
  strong {
    font-weight: 700;
  }
`;

/* 스텝 헤더(섹션 타이틀 + n / 3)
   태블릿 밴드(800~1279px)에서 폼이 풀블리드로 늘어지지 않도록 본문과 함께 640px 클램프 */
export const SectionRow = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: 20px 20px 12px;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Black};
`;

export const StepCount = styled.span`
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Gray30};

  b {
    color: ${({ theme }) => theme.color.Red};
  }
`;

/* 본문 (스크롤) — 태블릿 밴드에서 640px 컬럼으로 클램프 */
export const Body = styled.div`
  flex: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: 24px 20px 32px;
`;

/* 하단 고정 버튼 바 — 흰 바는 풀폭, 버튼 행만 본문 640px 컬럼과 정렬 */
export const Footer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 40;
  display: flex;
  padding-inline: max(0px, calc(50% - 300px));
  background: ${({ theme }) => theme.color.White};
`;

export const PrevButton = styled.button`
  flex: 0 0 35%;
  height: 60px;
  border: none;
  background: ${({ theme }) => theme.color.Gray20};
  color: ${({ theme }) => theme.color.Gray50};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.022em;
  cursor: pointer;
`;

export const NextButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  height: 60px;
  border: none;
  background: ${({ theme, $active }) => ($active === false ? theme.color.Gray30 : theme.color.Red)};
  color: ${({ theme }) => theme.color.White};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.022em;
  cursor: pointer;

  &:disabled {
    background: ${({ theme }) => theme.color.Gray30};
    cursor: default;
  }
`;

/* =========================================================================
 * 필드 (라벨 + 입력)
 * ======================================================================= */

export const Field = styled.div`
  display: flex;
  flex-direction: column;

  & + & {
    margin-top: 24px;
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Black};
`;

export const Req = styled.span`
  margin-left: 3px;
  color: ${({ theme }) => theme.color.Red};
`;

export const TextInput = styled.input<{ $invalid?: boolean }>`
  width: 100%;
  height: 54px;
  box-sizing: border-box;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.color.Red : theme.color.Gray20)};
  background: ${({ theme }) => theme.color.White};
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Black};

  &::placeholder {
    color: ${({ theme }) => theme.color.Gray30};
    font-weight: 500;
  }

  &:disabled {
    background: ${({ theme }) => theme.color.Gray20};
    color: ${({ theme }) => theme.color.Gray40};
    -webkit-text-fill-color: ${({ theme }) => theme.color.Gray40};
  }
`;

/* 주소(국문) + 주소검색, 이메일 + 인증요청 등 입력 우측 버튼 행 */
export const InlineRow = styled.div`
  display: flex;
  gap: 8px;
`;

export const DarkButton = styled.button`
  flex-shrink: 0;
  height: 54px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: #3a3a3a;
  color: ${({ theme }) => theme.color.White};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.022em;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    background: ${({ theme }) => theme.color.Gray30};
    cursor: default;
  }
`;

/* 입력란 우측에 단위(호 등)를 붙이는 래퍼 */
export const SuffixWrap = styled.div<{ $invalid?: boolean }>`
  display: flex;
  align-items: center;
  height: 54px;
  box-sizing: border-box;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.color.Red : theme.color.Gray20)};
  background: ${({ theme }) => theme.color.White};

  input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    background: transparent;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: -0.022em;
    color: ${({ theme }) => theme.color.Black};

    &::placeholder {
      color: ${({ theme }) => theme.color.Gray30};
    }
  }
`;

export const Suffix = styled.span`
  flex-shrink: 0;
  margin-left: 8px;
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.Gray40};
`;

export const Helper = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Gray40};
`;

export const ErrorText = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Red};
`;

/* =========================================================================
 * 선택 박스 그리드 (라디오: 중개사무소/숙박업소, 체크: 주거래매물·외국어)
 * ======================================================================= */

export const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const ChoiceBox = styled.button<{ $on?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 18px;
  border: 1px solid ${({ theme, $on }) => ($on ? theme.color.Red : theme.color.Gray20)};
  border-radius: 8px;
  background: ${({ theme }) => theme.color.White};
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Black};
  text-align: left;
  cursor: pointer;
`;

export const CheckIcon = styled.span<{ $on?: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1.5px solid ${({ theme, $on }) => ($on ? theme.color.Red : theme.color.Gray30)};
  background: ${({ theme, $on }) => ($on ? theme.color.Red : theme.color.White)};

  &::after {
    content: '';
    display: ${({ $on }) => ($on ? 'block' : 'none')};
    position: absolute;
    left: 6px;
    top: 2px;
    width: 5px;
    height: 9px;
    border: solid ${({ theme }) => theme.color.White};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

/* =========================================================================
 * 담당자 정보 (3/3)
 * ======================================================================= */

export const FieldCaption = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Gray40};
`;

export const ManagerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  & + & {
    margin-top: 10px;
  }
`;

export const ManagerInput = styled(TextInput)`
  flex: 1;
  min-width: 0;
`;

export const ManagerRemove = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.color.Gray30};
  background: ${({ theme }) => theme.color.White};
  color: ${({ theme }) => theme.color.Gray40};
  font-size: 22px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export const AddManagerButton = styled.button`
  margin-top: 12px;
  width: 100%;
  height: 56px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.PastelRed};
  color: ${({ theme }) => theme.color.Red};
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.022em;
  cursor: pointer;
`;

export const ManagerError = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Red};
`;

/* =========================================================================
 * 코드입력 / 이메일 인증 전용
 * ======================================================================= */

/* 본문을 위/아래로 밀어내는 스페이서 (제휴 요청 문구를 하단에 배치) */
export const Spacer = styled.div`
  flex: 1;
  min-height: 24px;
`;

export const PartnerInquiry = styled.div`
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Gray40};

  a {
    display: block;
    margin-top: 8px;
    color: ${({ theme }) => theme.color.Red};
    font-weight: 600;
    text-decoration: none;
  }
`;

/* 이메일 전송 안내 / 인증 실패 토스트 카드 (화면 하단) */
export const ToastCard = styled.div`
  margin-top: auto;
  padding: 20px;
  border-radius: 12px;
  background: ${({ theme }) => theme.color.White};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.color.Black};
`;
